import React, { useState } from 'react';
import { X, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { Button, Card, Input } from './';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  redirectToCheckout?: boolean;
}

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  redirectToCheckout = false,
}) => {
  const { login, isLoading, error, clearError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      await login(email, password);
      onClose();
      if (onSuccess) {
        onSuccess();
      }
      if (redirectToCheckout) {
        window.location.href = '/checkout';
      }
    } catch {
      // Error is handled by the store
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 animate-scale-in">
        <Card className="p-6 md:p-8 relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-dark-light dark:hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white dark:text-dark-dark mb-2 transition-colors duration-300">
              Welcome Back
            </h2>
            <p className="text-gray-400 dark:text-gray-500 transition-colors duration-300">
              Sign in to continue to checkout
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0" />
              <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon={<Mail className="w-5 h-5" />}
              placeholder="you@example.com"
              required
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<Lock className="w-5 h-5" />}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="rounded text-accent dark:text-primary"
                />
                <span className="text-sm text-gray-300 dark:text-gray-600">Remember me</span>
              </label>
              <a
                href="/forgot-password"
                className="text-sm text-accent dark:text-primary hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                  window.location.href = '/forgot-password';
                }}
              >
                Forgot password?
              </a>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Footer */}
          <p className="mt-6 text-center text-gray-400 dark:text-gray-500 transition-colors duration-300">
            Don't have an account?{' '}
            <button
              className="text-accent dark:text-primary hover:underline font-medium"
              onClick={() => {
                onClose();
                window.location.href = '/register';
              }}
            >
              Create one
            </button>
          </p>

          {/* Guest Checkout Option */}
          <div className="mt-6 pt-6 border-t border-dark-light dark:border-gray-200">
            <p className="text-center text-sm text-gray-400 dark:text-gray-500 mb-3">
              Or continue as guest
            </p>
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={() => {
                onClose();
                window.location.href = '/checkout';
              }}
            >
              Checkout as Guest
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LoginModal;

